import { useAuth } from '@/lib/auth'
import { useLocation } from 'react-router-dom'
import type { ReactNode } from 'react'

interface Props { children: ReactNode; title: string; action?: ReactNode }

export default function Layout({ children, title, action }: Props) {
  const { user, signOut } = useAuth()
  const { pathname } = useLocation()

  const adminLinks = [
    { href: '/', label: 'Dashboard', icon: 'dashboard' },
    { href: '/parts', label: 'Parts', icon: 'cog' },
    { href: '/vendors', label: 'Vendors', icon: 'building' },
    { href: '/wanted', label: 'Wanted Orders', icon: 'claims' },
    { href: '/brands', label: 'Brands', icon: 'flag' },
    { href: '/zones', label: 'Zones', icon: 'globe' },
    { href: '/blog', label: 'Blog', icon: 'blog' },
    { href: '/users', label: 'Users', icon: 'users' },
  ]

  const links = adminLinks
  const roleLabel = 'Admin'

  const icons: Record<string, ReactNode> = {
    dashboard: <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"/>,
    building: <path d="M3 21h18M4 3h14M5 3v18m12-18v18M8 7h2m-2 3h2m-2 3h2m4-6h2m-2 3h2m-2 3h2M8 21v-3c0-.6.4-1 1-1h4c.6 0 1 .4 1 1v3"/>,
    cog: <><path d="M10.34 4.03a1 1 0 01.93-.03l1.73.86 1.73-.86a1 1 0 011.4.42l.71 1.71 1.86.47a1 1 0 01.8 1.16l-.26 1.89 1.24 1.48a1 1 0 010 1.34l-1.24 1.48.26 1.89a1 1 0 01-.8 1.16l-1.86.47-.71 1.71a1 1 0 01-1.4.42l-1.73-.86-1.73.86a1 1 0 01-1.4-.42l-.71-1.71-1.86-.47a1 1 0 01-.8-1.16l.26-1.89-1.24-1.48a1 1 0 010-1.34l1.24-1.48-.26-1.89a1 1 0 01.8-1.16l1.86-.47.71-1.71a1 1 0 011.4-.42z"/><circle cx="12" cy="12" r="3"/></>,
    users: <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2M12 3a4 4 0 100 8 4 4 0 000-8zM22 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/>,
    claims: <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>,
    globe: <path d="M12 2a10 10 0 1010 10A10 10 0 0012 2zM2 12h20M12 2a15 15 0 014 10 15 15 0 01-4 10 15 15 0 01-4-10 15 15 0 014-10z"/>,
    flag: <path d="M4 21V4a1 1 0 011-1h4l2 2h9a1 1 0 011 1v10a1 1 0 01-1 1h-8l-2-2H5m4-6h6m-6 4h6"/>,
    blog: <path d="M4 5a2 2 0 012-2h12a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm4 3h8m-8 4h8m-8 4h5"/>,
  }

  return (
    <div className="min-h-screen bg-surface-50 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-slate-900 flex-shrink-0 hidden lg:flex flex-col">
        {/* Logo */}
        <div className="p-5 border-b border-white/5">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/25">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><path d="M5 11l1.5-4.5A2 2 0 018.4 5h7.2a2 2 0 011.9 1.5L19 11m-14 0h14m-14 0a2 2 0 00-2 2v5h3m13-7a2 2 0 012 2v5h-3m-13 0h13m-13 0a2 2 0 01-2 2H4m14 0a2 2 0 01-2 2h-1"/></svg>
            </div>
            <div>
              <p className="font-bold text-white text-sm tracking-tight">PartsMarket</p>
              <p className="text-[10px] text-slate-500 font-medium uppercase tracking-widest">{roleLabel}</p>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          <p className="px-3 mb-2 text-[10px] font-semibold text-slate-500 uppercase tracking-widest">Menu</p>
          {links.map(l => {
            const active = l.href === '/' ? pathname === '/' : pathname.startsWith(l.href)
            return (
              <a key={l.href} href={l.href}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 group ${
                  active
                    ? 'bg-brand-600 text-white shadow-lg shadow-brand-500/20'
                    : 'text-slate-400 hover:text-white hover:bg-white/5'
                }`}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  {icons[l.icon]}
                </svg>
                {l.label}
                {active && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-white/60" />}
              </a>
            )
          })}
        </nav>

        {/* User footer */}
        <div className="p-3 border-t border-white/5">
          <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white/[0.03]">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-xs font-bold text-white shadow">
              {(user?.email ?? 'A')[0].toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-white truncate">{user?.email ?? 'Admin'}</p>
              <button onClick={signOut} className="text-[10px] text-slate-500 hover:text-slate-300 transition-colors">Sign out</button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-surface-200">
          <div className="flex items-center justify-between h-14 px-6">
            <div className="flex items-center gap-4">
              <h1 className="text-base font-semibold text-slate-900 tracking-tight">{title}</h1>
              <span className="hidden sm:inline-flex items-center gap-1.5 text-[11px] font-medium text-slate-400 bg-surface-100 px-2.5 py-1 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                Live
              </span>
            </div>
            {action}
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 p-5 md:p-7">
          {children}
        </main>
      </div>
    </div>
  )
}
